import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Zoom,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BuyPlanButton from "../BuyPlanButton";
import RequestPlaneDialog from "./RequestPlaneDialog";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { API_BASE_URL_images } from "../../../Services/apiConfig";

const ProfessionalCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "20px",
  background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
  border: "1px solid rgba(0,0,0,0.05)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-12px) scale(1.02)",
    boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #1800ad 0%, #f57226 100%)",
  },
}));

const PriceBox = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
  borderRadius: "16px",
  padding: "20px",
  border: "2px solid #e9ecef",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-2px",
    left: "-2px",
    right: "-2px",
    bottom: "-2px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    zIndex: -1,
  },
}));

const PlanCard = ({ plan, index }) => {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);

  return (
    <Zoom in={true} timeout={500 + index * 200}>
      <ProfessionalCard>
        {plan.image && (
          <CardMedia
            component="img"
            height="220"
            image={`${API_BASE_URL_images}/${plan.image}`}
            alt={plan.plane_name}
            sx={{
              borderRadius: "20px 20px 0 0",
              objectFit: "cover",
            }}
          />
        )}
        <CardContent sx={{ flexGrow: 1, p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(90deg, #1800ad 0%, #f57226 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {plan.plane_name}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ minHeight: "48px", lineHeight: 1.6 }}
            >
              {plan.desc}
            </Typography>
          </Box>

          <PriceBox sx={{ mb: 3 }}>
            {(() => {
              const mOriginal = Number(plan.price) || 0;
              const yOriginal = Number(plan.yearly_price) || 0;
              const discount = Number(plan.descount) || 0;
              const mAfter = Math.round((mOriginal * (1 - discount / 100)) * 100) / 100;
              const yAfter = Math.round((yOriginal * (1 - discount / 100)) * 100) / 100;
              return (
                <>
                  <Box sx={{ textAlign: "center", mb: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "900",
                        background: "linear-gradient(90deg,#2e7d32,#156922)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      الشهري: {discount > 0 ? mAfter : mOriginal} جنيه
                    </Typography>
                    {discount > 0 && (
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", textDecoration: "line-through" }}>
                        {mOriginal} جنيه
                      </Typography>
                    )}
                  </Box>

                 

                  {discount > 0 && (
                    <Chip
                      label={`وفر ${discount}%`}
                      color="error"
                      sx={{
                        mt: 2,
                        display: "block",
                        mx: "auto",
                        width: "fit-content",
                        fontWeight: "bold",
                      }}
                    />
                  )}
                </>
              );
            })()}
          </PriceBox>

          {plan.features.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#1976d2",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                المميزات
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List sx={{ p: 0 }}>
                {plan.features.map((feature) => (
                  <ListItem
                    key={feature.id}
                    sx={{
                      py: 1,
                      px: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      mb: 1,
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        mb: 1,
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "#4caf50",
                          mr: 2,
                          fontSize: 20,
                        }}
                      />
                      <ListItemText
                        primary={feature.name}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "1rem",
                            fontWeight: 500,
                            color: "#555",
                          },
                        }}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* actions: شراء + طلب نسخة */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              mt: 3,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: { xs: "100%", sm: "auto" },
                justifyContent: "center",
              }}
            >
              <BuyPlanButton planeId={plan.id} />
            </Box>

            <Button
              variant="contained"
              onClick={() => setRequestDialogOpen(true)}
              startIcon={
                <RocketLaunchIcon sx={{ transform: "translateY(1px)" }} />
              }
              aria-label="طلب نسخة تجريبية لهذه الخطة"
              sx={{
                borderRadius: "32px",
                px: 4,
                py: 1.1,
                minWidth: { xs: "100%", sm: 200 },
                textTransform: "none",
                fontWeight: 700,
                color: "white",
                background: "linear-gradient(90deg, #1800ad 0%, #f57226 100%)",
                boxShadow: "0 8px 24px rgba(24,0,173,0.18)",
                transition:
                  "transform .15s ease, box-shadow .15s ease, opacity .15s",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 14px 40px rgba(245,114,38,0.18)",
                },
                "&:active": { transform: "scale(0.99)" },
              }}
            >
              اطلب هذه الخطة
            </Button>
          </Box>

          <RequestPlaneDialog
            open={requestDialogOpen}
            onClose={() => setRequestDialogOpen(false)}
            planeId={plan.id}
          />
        </CardContent>
      </ProfessionalCard>
    </Zoom>
  );
};

export default PlanCard;
